import { useState } from "react";
import { ContactList } from "../components/ContactList";
import { SelectionRescorePanel } from "../components/SelectionRescorePanel";
import { StatusLine, type StatusVariant } from "../components/StatusLine";
import {
  useContacts,
  useDebouncedValue,
  type ContactsQuery,
  type ContactsSort,
} from "../hooks/useContacts";
import { useIcps } from "../hooks/useIcps";
import type { IcpFilterStatus } from "../lib/types";

const DEFAULT_PAGE_SIZE = 100;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200, 500] as const;
const ANY_ICP = "" as const;
const SEARCH_DEBOUNCE_MS = 300;

const SORT_OPTIONS: ReadonlyArray<{ value: ContactsSort; label: string }> = [
  { value: "newest", label: "Processed: newest first" },
  { value: "oldest", label: "Processed: oldest first" },
  { value: "score_high", label: "Score: highest first" },
  { value: "score_low", label: "Score: lowest first" },
];

const STATUS_FILTERS: ReadonlyArray<{
  value: IcpFilterStatus;
  label: string;
  requiresIcp?: boolean;
}> = [
  { value: "all", label: "All" },
  { value: "pushed", label: "Pushed" },
  { value: "not_pushed", label: "Not pushed" },
  { value: "not_processed", label: "Not processed" },
  {
    value: "not_processed_matches",
    label: "Not processed (matches ICP apollo filters)",
    requiresIcp: true,
  },
];

export function Contacts() {
  const { data: icps } = useIcps();
  const [enabled, setEnabled] = useState(false);
  const [search, setSearch] = useState("");
  const [icpName, setIcpName] = useState<string>(ANY_ICP);
  const [status, setStatus] = useState<IcpFilterStatus>("all");
  const [scoreMin, setScoreMin] = useState<number | null>(null);
  const [scoreMax, setScoreMax] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<ContactsSort>("newest");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Debounce the search input so we don't refetch on every keystroke.
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const icpSelected = icpName !== ANY_ICP;
  const showScoreFilter =
    icpSelected &&
    status !== "not_processed" &&
    status !== "not_processed_matches";
  // Score range is meaningful only when an ICP is fixed and we're filtering
  // scored contacts; otherwise force it off so the request stays clean.
  const effectiveScoreMin = showScoreFilter ? scoreMin : null;
  const effectiveScoreMax = showScoreFilter ? scoreMax : null;

  const query: ContactsQuery = {
    page,
    pageSize,
    sort,
    search: debouncedSearch,
    icpName: icpSelected ? icpName : null,
    status,
    scoreMin: effectiveScoreMin,
    scoreMax: effectiveScoreMax,
  };

  const { data, isFetching, isError, error, refetch } = useContacts(
    query,
    enabled,
  );
  const contacts = data?.contacts ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);

  const loadStatus = computeLoadStatus({
    enabled,
    isFetching,
    isError,
    error: error ?? null,
    total,
  });

  const onLoad = () => {
    setEnabled(true);
    if (enabled) refetch();
  };

  const resetPage = () => setPage(1);

  const onSearchChange = (v: string) => {
    setSearch(v);
    resetPage();
  };

  const onIcpChange = (v: string) => {
    setIcpName(v);
    if (v === ANY_ICP && status === "not_processed_matches") {
      setStatus("all");
    }
    resetPage();
  };

  const onStatusChange = (s: IcpFilterStatus) => {
    setStatus(s);
    resetPage();
  };

  const onScoreChange = (key: "min" | "max", raw: string) => {
    const parsed = raw === "" ? null : Number(raw);
    const value = Number.isFinite(parsed) ? (parsed as number | null) : null;
    if (key === "min") setScoreMin(value);
    else setScoreMax(value);
    resetPage();
  };

  const onToggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Server-side filtering means we only have the current page in memory.
  // "Select all filtered" now means "select everyone on this page" — bulk
  // selection across all pages would need a separate ids-only endpoint.
  const onSelectAllFiltered = () => {
    setSelectedIds(new Set(contacts.map((c) => c.id)));
  };

  const onClearSelection = () => {
    setSelectedIds(new Set());
  };

  return (
    <section className="card">
      <h2>Pulled contacts</h2>
      <p className="card-desc">
        Every contact pulled from Apollo and scored. Filtering, sorting, and
        pagination all run server-side, so the page only loads the rows you're
        currently viewing.
      </p>
      <button onClick={onLoad} disabled={isFetching}>
        Load contacts
      </button>
      <StatusLine variant={loadStatus.variant} message={loadStatus.message} />

      <div className="field" style={{ marginTop: 16 }}>
        <label htmlFor="c-search">
          Search (<span>{total}</span> match)
        </label>
        <input
          type="text"
          id="c-search"
          placeholder="Filter by name, company, email…"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
        />
      </div>

      <div className="field">
        <label htmlFor="c-icp">ICP</label>
        <select
          id="c-icp"
          value={icpName}
          onChange={(e) => onIcpChange(e.target.value)}
        >
          <option value={ANY_ICP}>Any ICP</option>
          {(icps ?? []).map((icp) => (
            <option key={icp.name} value={icp.name}>
              {icp.display_name}
            </option>
          ))}
        </select>
      </div>

      <div className="filter-group">
        {STATUS_FILTERS
          .filter((filterOption) => !filterOption.requiresIcp || icpSelected)
          .map((filterOption) => (
            <button
              key={filterOption.value}
              className={
                "filter-btn" +
                (status === filterOption.value ? " active" : "")
              }
              onClick={() => onStatusChange(filterOption.value)}
            >
              {filterOption.label}
            </button>
          ))}
      </div>

      {showScoreFilter && (
        <div className="field-row" style={{ marginTop: 12 }}>
          <div className="field">
            <label htmlFor="c-score-min">Min score</label>
            <input
              type="number"
              id="c-score-min"
              placeholder="e.g. 50"
              value={scoreMin ?? ""}
              onChange={(e) => onScoreChange("min", e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="c-score-max">Max score</label>
            <input
              type="number"
              id="c-score-max"
              placeholder="e.g. 100"
              value={scoreMax ?? ""}
              onChange={(e) => onScoreChange("max", e.target.value)}
            />
          </div>
        </div>
      )}

      <div className="field-row" style={{ marginTop: 12 }}>
        <div className="field" style={{ maxWidth: 280 }}>
          <label htmlFor="c-sort">Sort by</label>
          <select
            id="c-sort"
            value={sort}
            onChange={(e) => {
              setSort(e.target.value as ContactsSort);
              resetPage();
            }}
          >
            {SORT_OPTIONS.map(({ value, label }) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ maxWidth: 200 }}>
          <label htmlFor="c-page-size">Per page</label>
          <select
            id="c-page-size"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              resetPage();
            }}
          >
            {PAGE_SIZE_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </div>
      </div>

      {enabled && !isError && (
        <>
          <SelectionRescorePanel
            selectedIds={selectedIds}
            filteredCount={contacts.length}
            onSelectAllFiltered={onSelectAllFiltered}
            onClear={onClearSelection}
          />
          <ContactList
            contacts={contacts}
            totalCount={total}
            page={safePage}
            pageSize={pageSize}
            totalPages={totalPages}
            onPageChange={setPage}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
          />
        </>
      )}
    </section>
  );
}

function computeLoadStatus(args: {
  enabled: boolean;
  isFetching: boolean;
  isError: boolean;
  error: Error | null;
  total: number;
}): { variant: StatusVariant; message: string } {
  if (!args.enabled) return { variant: "idle", message: "" };
  if (args.isFetching) return { variant: "running", message: "Loading…" };
  if (args.isError) {
    return {
      variant: "error",
      message: `Failed to load: ${args.error?.message ?? "unknown"}`,
    };
  }
  return {
    variant: "success",
    message: `${args.total} contacts match.`,
  };
}
